import { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

type DebugRequestConfig = InternalAxiosRequestConfig & {
  __debugRequestStartedAt?: number;
  __networkRetryCount?: number;
}

const SENSITIVE_HEADERS = ['authorization', 'cookie', 'set-cookie', 'x-api-key']

const isDebugLoggingEnabled = () => process.env.NODE_ENV !== 'production'

const getRequestLabel = (config: Partial<InternalAxiosRequestConfig>) => {
  const method = config.method?.toUpperCase() ?? 'GET'
  const url = `${config.baseURL ?? ''}${config.url ?? ''}`

  return `${method} ${url}`
}

const getDuration = (config?: DebugRequestConfig) => {
  if (!config?.__debugRequestStartedAt) return undefined

  return `${Date.now() - config.__debugRequestStartedAt}ms`
}

const sanitizeHeaders = (headers?: InternalAxiosRequestConfig['headers']) => {
  if (!headers) return undefined

  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      SENSITIVE_HEADERS.includes(key.toLowerCase()) ? '[redacted]' : value
    ])
  )
}

export const addAxiosDebugInterceptors = (
  client: AxiosInstance,
  clientName: string
) => {
  client.interceptors.request.use((config: DebugRequestConfig) => {
    if (!isDebugLoggingEnabled()) return config

    config.__debugRequestStartedAt = Date.now()

    console.debug(`[axios:${clientName}] -> ${getRequestLabel(config)}`, {
      params: config.params,
      headers: sanitizeHeaders(config.headers),
      retryCount: config.__networkRetryCount ?? 0
    })

    return config
  })

  client.interceptors.response.use(
    (response: AxiosResponse) => {
      if (isDebugLoggingEnabled()) {
        console.debug(`[axios:${clientName}] <- ${getRequestLabel(response.config)} ${response.status}`, {
          duration: getDuration(response.config as DebugRequestConfig)
        })
      }

      return response
    },
    (error: AxiosError) => {
      if (isDebugLoggingEnabled()) {
        console.debug(`[axios:${clientName}] xx ${getRequestLabel(error.config ?? {})}`, {
          status: error.response?.status,
          code: error.code,
          message: error.message,
          duration: getDuration(error.config as DebugRequestConfig | undefined),
          retryCount: (error.config as DebugRequestConfig | undefined)?.__networkRetryCount ?? 0
        })
      }

      return Promise.reject(error)
    }
  )
}
